import { Popup } from 'semantic-ui-react';
import styled from 'styled-components';
import React, { useState, type ReactNode } from 'react';
import { useDebounce } from '../hooks/debounce';

interface IDarkPopupProps {
  children: ReactNode;
  contentWidth?: number;
  contentMaxHight?: string;
  padding?: string;
  setOpen?: (open: boolean) => void;
  primaryDark?: boolean;
  // Add any other props from Popup if needed
  [key: string]: any;
}

const Popper = styled.div`
  opacity: 0;
  pointer-events: none;
  &.show {
    opacity: 1;
    pointer-events: all;
  }
`;

interface IDarkPopupContentProps {
  padding?: string;
  primaryDark?: boolean;
  contentWidth?: number;
  maxHight?: string;
}

const DarkPopupContent = styled.div<IDarkPopupContentProps>`
  background-color: ${({ theme, primaryDark }) =>
    primaryDark ? theme.colors.primary : theme.colors.lightBlack};
  padding: ${({ padding }) => padding ?? '1rem'};
  color: ${({ theme }) => theme.colors.white};
  ${({ contentWidth }) => contentWidth && `max-width: ${contentWidth}px;`}
  max-height: ${({ maxHight }) => maxHight || '350px'};
  overflow-y: auto;
  overflow-x: hidden;
  border-radius: 5px;
`;

interface IStyledDarkPopupProps {
  primaryDark?: boolean;
}

const StyledDarkPopup = styled(Popup)<IStyledDarkPopupProps>`
  &:before {
    background: ${({ theme, primaryDark }) =>
      primaryDark ? theme.colors.primary : theme.colors.lightBlack} !important;
  }
`;

export function DarkPopup({
  children,
  contentWidth,
  contentMaxHight,
  padding,
  setOpen,
  primaryDark = false,
  ...props
}: IDarkPopupProps) {
  const [show, setShow] = useState<boolean>(false);
  const finalShow = useDebounce(show, 50);
  const childrenWithProps = React.Children.map(children, child => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child as React.ReactElement<any>, { setShow });
    }
    return child;
  });

  const handleOpen = () => {
    setShow(true);
    if (typeof setOpen === 'function') {
      setOpen(true);
    }
  };

  const handleClose = () => {
    setShow(false);
    if (typeof setOpen === 'function') {
      setOpen(false);
    }
  };

  return (
    <StyledDarkPopup
      inverted
      style={{
        padding: 0,
      }}
      onOpen={handleOpen}
      onClose={handleClose}
      primaryDark={primaryDark}
      popper={<Popper className={finalShow ? 'show' : ''} />}
      {...props}
    >
      <DarkPopupContent
        padding={padding}
        primaryDark={primaryDark}
        contentWidth={contentWidth}
        maxHight={contentMaxHight}
      >
        {childrenWithProps}
      </DarkPopupContent>
    </StyledDarkPopup>
  );
}
